import { useEffect } from 'react';
import {
  type PendingPrompt,
  type StickyEditScope,
} from '@/stores/agentChatAguiStore';
import {
  PENDING_PROMPT_MAX_AGE_MS,
  PENDING_PROMPT_SESSION_KEY,
} from '@/stores/pendingPromptStorage';
import type { AzureContext } from '@/types/agui';

/**
 * Session-storage fallback for a pending prompt when Zustand has no live
 * pending prompt at mount time. Handles the page-refresh scenario where
 * the user picked a prompt from an entry point that navigated to the
 * designer route via a hard reload.
 *
 * Prompts older than {@link PENDING_PROMPT_MAX_AGE_MS} are ignored — treats
 * them as stale and drops them.
 *
 * The session-storage key and TTL live in `@/stores/pendingPromptStorage`
 * so the store (writer, in `agentChatAguiStore`) and this hook (reader)
 * share one source of truth for the key. The store references the raw
 * literal for the same value; keep them in sync.
 *
 * Exported for direct testing; consumers typically go through
 * `useConsumePendingPrompt` below.
 */
export interface HydratedPendingPrompt {
  content: string;
  scope: StickyEditScope;
  source: PendingPrompt['source'];
  systemContext?: string | undefined;
}

export function hydratePendingPromptFromStorage(
  now: number = Date.now()
): HydratedPendingPrompt | null {
  try {
    const raw = sessionStorage.getItem(PENDING_PROMPT_SESSION_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as PendingPrompt & { storedAt?: number };
    const age = now - (stored.storedAt ?? 0);
    sessionStorage.removeItem(PENDING_PROMPT_SESSION_KEY);
    if (age >= PENDING_PROMPT_MAX_AGE_MS) return null;
    return {
      content: stored.content,
      scope: stored.scope,
      source: stored.source,
      systemContext: stored.systemContext,
    };
  } catch {
    return null;
  }
}

export interface ConsumePendingPromptDeps {
  /** Live pending prompt from the store, if any. */
  pendingPrompt: PendingPrompt | null;
  /** Current workflow name — empty/undefined means "no workflow selected yet". */
  workflowName: string | undefined;
  /** Whether the assistant is currently streaming a response. */
  isStreaming: boolean;
  /** Azure context bits (fall back to local-* when running outside portal). */
  subscriptionId: string | undefined;
  resourceGroup: string | undefined;
  siteName: string | undefined;
  hostName: string | undefined;
  location: string | undefined;

  /** Actions the effect performs. */
  setIsChatSidebarOpen: (open: boolean) => void;
  setHasUnreadMessages: (value: boolean) => void;
  sendMessage: (
    content: string,
    getToken: () => Promise<string | null>,
    ctx: AzureContext,
    scope?: StickyEditScope
  ) => void | Promise<void>;
  getToken: () => Promise<string | null>;
  clearPendingPrompt: () => void;
}

/**
 * Result of the "should we consume a pending prompt on this render?" decision
 * — exported so callers can unit-test the branching without needing React.
 */
export type ConsumePendingPromptOutcome =
  | { kind: 'no-prompt' }
  | { kind: 'streaming-drop' }
  | { kind: 'open-no-workflow' }
  | {
      kind: 'send';
      content: string;
      scope: StickyEditScope | undefined;
      azureContext: AzureContext;
    };

export function decideConsumePendingPrompt(
  deps: Omit<ConsumePendingPromptDeps, 'setIsChatSidebarOpen' | 'setHasUnreadMessages' | 'sendMessage' | 'getToken' | 'clearPendingPrompt'>,
  hydrated: HydratedPendingPrompt | null
): ConsumePendingPromptOutcome {
  const prompt = deps.pendingPrompt ?? hydrated;
  if (!prompt) return { kind: 'no-prompt' };
  if (deps.isStreaming) return { kind: 'streaming-drop' };
  if (!deps.workflowName) return { kind: 'open-no-workflow' };
  const azureContext: AzureContext = {
    subscriptionId: deps.subscriptionId ?? 'local-subscription',
    resourceGroup: deps.resourceGroup ?? 'local-resource-group',
    siteName: deps.siteName ?? 'local-runtime',
    location: deps.location ?? 'local',
    workflowName: deps.workflowName,
    hostName: deps.hostName ?? '',
  };
  return {
    kind: 'send',
    content: prompt.content,
    scope: prompt.scope,
    azureContext,
  };
}

/**
 * Consumes pending prompts arriving from AI entry points (picker, node editor,
 * workflow-list-hero, etc.). Fires when `pendingPrompt` changes; also hydrates
 * from `sessionStorage` on mount to survive a full page reload.
 *
 * Extracted from `WorkflowDesignerWithCode` so the branching is testable in
 * isolation without a 3000-line render tree.
 */
export function useConsumePendingPrompt(deps: ConsumePendingPromptDeps): void {
  const {
    pendingPrompt,
    workflowName,
    isStreaming,
    subscriptionId,
    resourceGroup,
    siteName,
    hostName,
    location,
    setIsChatSidebarOpen,
    setHasUnreadMessages,
    sendMessage,
    getToken,
    clearPendingPrompt,
  } = deps;

  useEffect(() => {
    const hydrated = pendingPrompt ? null : hydratePendingPromptFromStorage();
    const outcome = decideConsumePendingPrompt(
      {
        pendingPrompt,
        workflowName,
        isStreaming,
        subscriptionId,
        resourceGroup,
        siteName,
        hostName,
        location,
      },
      hydrated
    );

    switch (outcome.kind) {
      case 'no-prompt':
        return;
      case 'streaming-drop':
        clearPendingPrompt();
        return;
      case 'open-no-workflow':
        setIsChatSidebarOpen(true);
        setHasUnreadMessages(false);
        clearPendingPrompt();
        return;
      case 'send':
        setIsChatSidebarOpen(true);
        setHasUnreadMessages(false);
        sendMessage(outcome.content, getToken, outcome.azureContext, outcome.scope);
        clearPendingPrompt();
        return;
    }
  }, [
    pendingPrompt,
    workflowName,
    isStreaming,
    subscriptionId,
    resourceGroup,
    siteName,
    hostName,
    location,
    setIsChatSidebarOpen,
    setHasUnreadMessages,
    sendMessage,
    getToken,
    clearPendingPrompt,
  ]);
}
